import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType } from 'vopay';
import EftWithdrawTransactionInfo from './EftWithdrawTransactionInfo';
import useFetchEFTWithdrawTransaction from '@/hooks/useApi/useFetchEFTWithdrawTransaction';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type EftWithdrawTransactionProps = {
  TransactionID: string;
  isParent?: boolean;
} & Omit<
  ComponentProps<typeof EftWithdrawTransactionInfo>,
  'transaction' | 'isParent' | 'parentTransactionId' | 'parentType'
>;

export default function EftWithdrawTransactionContainer({
  TransactionID,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  ...props
}: EftWithdrawTransactionProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { eftWithdrawTransaction, fetchEftWithdrawTransaction } = useFetchEFTWithdrawTransaction({ bearer: jwt });

  useEffect(() => {
    blockingLoader.show();

    fetchEftWithdrawTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = eftWithdrawTransaction;

  const fundingParentTransaction = Object.values(transaction?.ParentTransaction || {}).find(
    (parent) => parent.TransactionType === TransactionType.EFT_FUNDING,
  );

  return (
    <div>
      {transaction && (
        <EftWithdrawTransactionInfo
          transaction={transaction}
          Notes={Notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          parentTransactionId={fundingParentTransaction?.TransactionID}
          parentType={fundingParentTransaction ? 'EFT Funding' : undefined}
          {...props}
        />
      )}
    </div>
  );
}
